import format from 'date-fns/format';
import * as dateFnsLocales from 'date-fns/locale';
import i18next from 'i18next';
import moment from 'moment';
import {COUNTRY_CODES} from './constants';

export type ErrorType =
  | {
      message: string;
      errors: any;
      details: string;
    }
  | string;

function getErrorMessage(error: ErrorType) {
  let message = '';

  if (!error) {
    return message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error?.details) {
    return error?.details;
  }
  if (error?.errors?.length) {
    const nonFieldErrors = 'non_field_errors';
    Object.values(error.errors).forEach((err: any) => {
      message += ` ${err.field && err.field !== nonFieldErrors ? `${err.field}:` : ''} ${
        err.message
      }\n`;
    });
  } else if (error?.message) {
    message = error.message;
  } else {
    if (typeof error === 'object') {
      Object.values(error).forEach(err => {
        message += ` ${err}\n`;
      });
    }
  }

  if (typeof message === 'object') {
    return '';
  }

  if (message?.startsWith('Unexpected JSON')) {
    return 'Server error.';
  }

  return message;
}

function getURLSearchParam(name: string, source = window.location.search) {
  const searchParams = new URLSearchParams(source);
  return searchParams.get(name);
}

function setURLSearchParam(name: string, value = '', target = window.location.search) {
  const searchParams = new URLSearchParams(target);
  return searchParams.set(name, value);
}

function deleteQueryParam(name: string, source = window.location.search) {
  const searchParams = new URLSearchParams(source);
  searchParams.delete(name);

  return searchParams.toString();
}

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

function getCurrentLocale() {
  try {
    return i18next.language.slice(0, 2);
  } catch (e) {
    return 'en';
  }
}

function getEnvVariable(key: string) {
  const variable = process.env[key];

  if (!variable) {
    throw new Error(`${key} .env variable is missing.`);
  }

  return variable;
}

function getMinCheckInDate(countryCode?: string) {
  let date;

  if (countryCode === COUNTRY_CODES.portugal) {
    date = moment().subtract(4, 'days');
  } else {
    date = moment().subtract(1, 'days').startOf('day');
  }

  return date;
}

function copyToClipboard(value: string) {
  const el = document.createElement('textarea');
  el.value = value;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

function addFocusTypeDetection(): () => void {
  const setUtilityFocus = () => {
    document.body.classList.add('utilityfocus');
    document.body.classList.remove('pointerfocus');
  };
  const setPointerFocus = () => {
    document.body.classList.add('pointerfocus');
    document.body.classList.remove('utilityfocus');
  };

  document.addEventListener('keydown', setUtilityFocus);
  document.addEventListener('mousedown', setPointerFocus);

  return () => {
    document.removeEventListener('keydown', setUtilityFocus);
    document.removeEventListener('mousedown', setPointerFocus);
  };
}

function formatDatePeriod(dateIn: Date, dateOut: Date): string {
  const isDiffMonth = dateIn.getMonth() !== dateOut.getMonth();

  if (isDiffMonth) {
    return `${format(dateIn, 'MMM dd')}-${format(dateOut, 'MMM dd, yyyy')}`;
  }

  return format(dateIn, `MMM dd-${format(dateOut, 'dd')}, yyyy`);
}

type FormatOneDate = {
  date: Date | null;
  housingCountry?: string;
  ipCountry?: string;
  locale?: string;
};
function formatOneDate({date, housingCountry, ipCountry, locale}: FormatOneDate): string {
  if (!date) return '';
  const isUsaCountry =
    housingCountry === COUNTRY_CODES.usa || ipCountry === COUNTRY_CODES.usa;
  const formatTemplate = isUsaCountry ? 'MMM dd, yyyy' : 'dd MMM, yyyy';

  return `${format(date, formatTemplate, {
    // @ts-ignore
    locale: dateFnsLocales[locale],
  })}`;
}

function extractUTMParams() {
  const utm = {
    'UTM Source': getURLSearchParam('utm_source'),
    'UTM Medium': getURLSearchParam('utm_medium'),
    'UTM Campaign': getURLSearchParam('utm_campaign'),
  };

  if (utm['UTM Source'] || utm['UTM Medium'] || utm['UTM Campaign']) {
    localStorage.setItem('user_utm_data', JSON.stringify(utm));
    return utm;
  }

  return {};
}

function downloadFromLink(link?: string) {
  if (!link) return;
  const isSwitched = window.open(link, '_blank');
  if (!isSwitched) {
    const anchorLink = document.createElement('a');
    anchorLink.href = link;
    anchorLink.click();
  }
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = error => reject(error);
  });
}

function base64ToFile(
  base64: string,
  fileName = Date.now().toString(),
  defaultType?: string,
) {
  const base64Parts = base64.split(',');
  const fileFormat = base64Parts[0].split(':')[1].split(';')[0];
  const fileContent = base64Parts[1];

  const file = new File([fileContent], fileName, {type: defaultType || fileFormat});
  return file;
}

const comaInterval = 3;
const comaOffset = 2;
const isWithoutComa = (index: number) => (index + comaOffset) % comaInterval;
function numberWithComas(n: number) {
  return String(n)
    .split('')
    .reduce((resultingString, currentChar, idx) => {
      if (isWithoutComa(idx)) return resultingString + currentChar;
      return resultingString + `,${currentChar}`;
    });
}

function getTextDescriptionNumberOfGuests(
  numberOfGuests: number,
  {language, t}: {language: string; t: (text: string) => string},
) {
  const guestText =
    language === COUNTRY_CODES.germany.toLowerCase()
      ? t('guest')
      : t('guest').toLowerCase();
  const text = numberOfGuests === 1 ? guestText : t('guests_count').toLowerCase();

  return `${numberOfGuests} ${text}`;
}

function getFileSizeMB(file?: File | Blob) {
  if (!file) return;
  return file.size / Math.pow(1024, 2);
}

export {
  getTextDescriptionNumberOfGuests,
  numberWithComas,
  downloadFromLink,
  toBase64,
  base64ToFile,
  getErrorMessage,
  getURLSearchParam,
  setURLSearchParam,
  getCurrentLocale,
  parseJwt,
  getEnvVariable,
  getMinCheckInDate,
  deleteQueryParam,
  copyToClipboard,
  addFocusTypeDetection,
  formatDatePeriod,
  formatOneDate,
  extractUTMParams,
  getFileSizeMB,
};
